import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { aiService, authService, roomService, projectService } from '../api/services';
import RoomAvailabilitySwitch from '../components/RoomAvailabilitySwitch';
import ListingProjectImport from '../components/ListingProjectImport';
import projectAmenities from '../data/projectAmenities';
import { furnishings } from '../components/ProjectFurnishings';
import ListingFee from '../components/ListingFee';
import { newDraftId } from '../utils/depositDrafts';
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { translateApiMessage, useLanguage } from '../contexts/LanguageContext';
import hcmWardMappings from '../data/hcmWardMappings';

const defaultCenter = [10.8231, 106.6297];
const cityCoordinates = {
  'ha noi': [21.0278, 105.8342],
  hanoi: [21.0278, 105.8342],
  'ho chi minh': [10.8231, 106.6297],
  hcm: [10.8231, 106.6297],
  'da nang': [16.0544, 108.2022],
  'hai phong': [20.8449, 106.6881],
  'can tho': [10.0452, 105.7469],
  'nha trang': [12.2388, 109.1967],
};

const hcmDistrictCoordinates = {
  1: [10.7757, 106.7004],
  2: [10.7872, 106.7498],
  3: [10.7842, 106.6847],
  4: [10.7599, 106.7032],
  5: [10.7540, 106.6665],
  6: [10.7467, 106.6355],
  7: [10.7342, 106.7218],
  8: [10.7248, 106.6286],
  9: [10.8428, 106.8287],
  10: [10.7723, 106.6677],
  11: [10.7649, 106.6464],
  12: [10.8672, 106.6419],
};

const hcmAreaNameCoordinates = {
  'thu duc': [10.8494, 106.7537],
  'go vap': [10.8388, 106.6655],
  'binh thanh': [10.8105, 106.7091],
  'tan binh': [10.8036, 106.6520],
  'tan phu': [10.7919, 106.6286],
  'phu nhuan': [10.7991, 106.6801],
  'binh tan': [10.7656, 106.6023],
  'binh chanh': [10.6907, 106.5993],
  'hoc mon': [10.8833, 106.5833],
  'nha be': [10.6645, 106.7323],
  'cu chi': [11.0323, 106.5018],
  'can gio': [10.4114, 106.9547],
};

const hanoiAreaNameCoordinates = {
  'ba dinh': [21.0368, 105.8342],
  'hoan kiem': [21.0288, 105.8524],
  'tay ho': [21.0703, 105.8156],
  'long bien': [21.0469, 105.8896],
  'cau giay': [21.0305, 105.7902],
  'dong da': [21.0181, 105.8292],
  'hai ba trung': [21.0058, 105.8578],
  'hoang mai': [20.9746, 105.8639],
  'thanh xuan': [20.9930, 105.8040],
  'nam tu liem': [21.0036, 105.7662],
  'bac tu liem': [21.0710, 105.7718],
  'ha dong': [20.9710, 105.7797],
};

const vietnamBounds = [
  [8.3, 102.0],
  [23.6, 110.5],
];

const sortVietnamese = (items) => [...items].sort((a, b) => a.localeCompare(b, 'vi', { sensitivity: 'base' }));

const clampToVietnamBounds = (coordinates) => {
  if (!Array.isArray(coordinates) || coordinates.length !== 2) {
    return [10.8231, 106.6297];
  }

  const latitude = Number(coordinates[0]);
  const longitude = Number(coordinates[1]);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return [10.8231, 106.6297];
  }

  return [
    Math.max(vietnamBounds[0][0], Math.min(vietnamBounds[1][0], latitude)),
    Math.max(vietnamBounds[0][1], Math.min(vietnamBounds[1][1], longitude)),
  ];
};

const pickerIcon = L.divIcon({
  className: 'location-picker-marker-wrap',
  html: '<span class="location-picker-marker"></span>',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

const normalizeCity = (city) => String(city || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/đ/g, 'd')
  .replace(/Đ/g, 'D')
  .toLowerCase()
  .trim();

const normalizeDistrict = (district) => String(district || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/đ/g, 'd')
  .replace(/Đ/g, 'D')
  .toLowerCase()
  .trim();

const normalizeText = (value) => normalizeDistrict(value);

const getCityMatchKeys = (cityRaw) => {
  const city = normalizeText(cityRaw);
  if (!city) {
    return [];
  }

  const keys = [city];
  if (city === 'hcm' || city.includes('ho chi minh')) {
    keys.push('ho chi minh');
    keys.push('thanh pho ho chi minh');
    keys.push('tp ho chi minh');
    keys.push('sai gon');
  }
  if (city === 'ha noi' || city === 'hanoi') {
    keys.push('ha noi');
    keys.push('hanoi');
    keys.push('thanh pho ha noi');
    keys.push('tp ha noi');
  }

  return [...new Set(keys)];
};

const getResultAdminHaystack = (result) => normalizeText([
  result?.address?.city,
  result?.address?.county,
  result?.address?.state,
  result?.address?.state_district,
  result?.address?.city_district,
  result?.address?.suburb,
  result?.address?.town,
  result?.address?.village,
].filter(Boolean).join(' '));

const buildResultHaystack = (result) => normalizeText([
  result?.display_name,
  result?.name,
  result?.address?.road,
  result?.address?.suburb,
  result?.address?.city,
  result?.address?.county,
  result?.address?.state,
  result?.address?.state_district,
  result?.address?.town,
  result?.address?.village,
].filter(Boolean).join(' '));

const matchesDistrict = (haystack, districtRaw) => {
  const districtKey = normalizeText(districtRaw);
  if (!districtKey) {
    return true;
  }

  const keys = getDistrictMatchKeys(districtRaw);
  return keys.some((key) => haystack.includes(key));
};

const matchesCity = (haystack, cityRaw) => {
  const cityKeys = getCityMatchKeys(cityRaw);
  if (cityKeys.length === 0) {
    return true;
  }
  return cityKeys.some((key) => haystack.includes(key));
};

const filterResultsByAdmin = (results, districtRaw, cityRaw, strictCity = false) => {
  if (!Array.isArray(results) || results.length === 0) {
    return [];
  }

  const districtText = normalizeText(districtRaw);
  const cityText = normalizeText(cityRaw);

  return results.filter((result) => {
    const adminHaystack = getResultAdminHaystack(result);

    if (districtText && !matchesDistrict(adminHaystack, districtRaw)) {
      return false;
    }

    if ((strictCity || cityText) && !matchesCity(adminHaystack, cityRaw)) {
      return false;
    }

    return true;
  });
};

const getCityQueryText = (cityRaw) => {
  const city = normalizeText(cityRaw);
  if (!city) {
    return '';
  }

  if (city === 'hcm' || city.includes('ho chi minh')) {
    return 'Ho Chi Minh';
  }

  if (city === 'ha noi' || city === 'hanoi') {
    return 'Ha Noi';
  }

  return String(cityRaw || '').trim();
};

const getDistrictMatchKeys = (districtRaw) => {
  const text = normalizeText(districtRaw);
  if (!text) {
    return [];
  }

  const keys = [text];
  const number = parseDistrictNumber(districtRaw);
  if (number) {
    keys.push(`quan ${number}`);
    keys.push(`q ${number}`);
    keys.push(`q${number}`);
    keys.push(`district ${number}`);
    keys.push(`district${number}`);
  }

  return [...new Set(keys)];
};

const pickBestGeocodeResult = (results, addressRaw, districtRaw, cityRaw) => {
  if (!Array.isArray(results) || results.length === 0) {
    return null;
  }

  const addressTokens = normalizeText(addressRaw)
    .split(/\s+/)
    .map((token) => token.trim())
    .filter((token) => token.length >= 2);
  const districtKey = normalizeText(districtRaw);
  const districtMatchKeys = getDistrictMatchKeys(districtRaw);
  const cityMatchKeys = getCityMatchKeys(cityRaw);

  const hasDistrictMatch = (haystack) => {
    if (!districtKey) {
      return true;
    }
    return districtMatchKeys.some((key) => haystack.includes(key));
  };

  const hasCityMatch = (haystack) => {
    if (cityMatchKeys.length === 0) {
      return true;
    }
    return cityMatchKeys.some((key) => haystack.includes(key));
  };

  const scoreResult = (result) => {
    const haystack = buildResultHaystack(result);
    const adminHaystack = getResultAdminHaystack(result);
    let score = Number(result?.importance || 0);

    if (hasCityMatch(adminHaystack)) {
      score += 9;
    }

    addressTokens.forEach((token) => {
      if (haystack.includes(token)) {
        score += 4;
      }
    });

    if (hasDistrictMatch(adminHaystack)) {
      score += 1.5;
    }

    const type = String(result?.type || '').toLowerCase();
    if (['road', 'residential', 'secondary', 'primary', 'tertiary', 'trunk', 'pedestrian'].includes(type)) {
      score += 1.5;
    }

    return score;
  };

  const strictCityOnly = results.filter((item) => {
    const adminHaystack = getResultAdminHaystack(item);
    return hasCityMatch(adminHaystack);
  });

  const strictDistrictOnly = results.filter((item) => {
    const adminHaystack = getResultAdminHaystack(item);
    return hasDistrictMatch(adminHaystack);
  });

  const pool = strictCityOnly.length > 0
    ? strictCityOnly
    : strictDistrictOnly.length > 0
      ? strictDistrictOnly
      : results;

  return [...pool].sort((a, b) => scoreResult(b) - scoreResult(a))[0] || pool[0] || results[0];
};

const parseDistrictNumber = (district) => {
  const text = normalizeDistrict(district);
  const direct = text.match(/^\d{1,2}$/);
  if (direct) {
    return Number(direct[0]);
  }

  const formatted = text.match(/^(?:q|quan|district)\s*(\d{1,2})$/);
  if (formatted) {
    return Number(formatted[1]);
  }

  return null;
};

const normalizeAdministrativeName = (district) => normalizeDistrict(district)
  .replace(/^(?:quan|q|huyen|h|thi xa|tx|thanh pho|tp)\s+/g, '')
  .replace(/\s+/g, ' ')
  .trim();

const resolveDistrictFallbackCenter = (district, city) => {
  const cityText = normalizeCity(city);
  const districtNumber = parseDistrictNumber(district);
  const normalizedName = normalizeAdministrativeName(district);

  const isHcmContext = !cityText || cityText.includes('ho chi minh') || cityText === 'hcm';
  const isHanoiContext = !cityText || cityText.includes('ha noi') || cityText === 'hanoi';

  if (isHcmContext && districtNumber) {
    const byNumber = hcmDistrictCoordinates[districtNumber];
    if (byNumber) {
      return byNumber;
    }
  }

  if (!normalizedName) {
    return null;
  }

  if (isHcmContext && hcmAreaNameCoordinates[normalizedName]) {
    return hcmAreaNameCoordinates[normalizedName];
  }

  if (isHanoiContext && hanoiAreaNameCoordinates[normalizedName]) {
    return hanoiAreaNameCoordinates[normalizedName];
  }

  return null;
};

const normalizeLongitude = (lng) => {
  const value = Number(lng);
  if (!Number.isFinite(value)) {
    return null;
  }
  return ((((value + 180) % 360) + 360) % 360) - 180;
};

const normalizeLatitude = (lat) => {
  const value = Number(lat);
  if (!Number.isFinite(value)) {
    return null;
  }
  return Math.max(-90, Math.min(90, value));
};

const normalizeCoordinates = (lat, lng) => {
  const normalizedLat = normalizeLatitude(lat);
  const normalizedLng = normalizeLongitude(lng);
  if (normalizedLat === null || normalizedLng === null) {
    return null;
  }
  return clampToVietnamBounds([normalizedLat, normalizedLng]);
};

const resolveGeocodeCenter = (result) => {
  const bbox = Array.isArray(result?.boundingbox) ? result.boundingbox : null;
  if (bbox && bbox.length === 4) {
    const south = Number(bbox[0]);
    const north = Number(bbox[1]);
    const west = Number(bbox[2]);
    const east = Number(bbox[3]);

    if ([south, north, west, east].every(Number.isFinite)) {
      return clampToVietnamBounds([(south + north) / 2, (west + east) / 2]);
    }
  }

  const lat = Number(result?.lat);
  const lng = Number(result?.lon);
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return clampToVietnamBounds([lat, lng]);
  }

  return null;
};

const MapClickHandler = ({ onPick }) => {
  useMapEvents({
    click(event) {
      const next = normalizeCoordinates(event.latlng.lat, event.latlng.lng);
      if (next) {
        onPick(next);
      }
    },
  });

  return null;
};

const MapViewportController = ({ center, zoom }) => {
  const map = useMap();

  useEffect(() => {
    const resize = () => map.invalidateSize({ pan: false });
    const frame = requestAnimationFrame(resize);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null;
    observer?.observe(map.getContainer());
    window.addEventListener('resize', resize);
    return () => { cancelAnimationFrame(frame); observer?.disconnect(); window.removeEventListener('resize', resize); };
  }, [map]);

  useEffect(() => {
    if (!Array.isArray(center) || center.length !== 2) {
      return;
    }

    map.flyTo(center, zoom, { duration: 0.45 });
  }, [map, center, zoom]);

  return null;
};

const FURNISHING_ITEMS = ['Giường', 'Nệm', 'Tủ quần áo', 'Bàn ghế', 'Máy lạnh', 'Tủ lạnh', 'Máy nước nóng', 'Bếp', 'Máy giặt', 'Rèm cửa'];
const BUILDING_UTILITIES = projectAmenities.map(([, label]) => label);

const parseMoneyValue = (value) => {
  const digits = String(value ?? '').replace(/[^\d-]/g, '');
  return digits ? Number(digits) : 0;
};

const CreateRoom = ({ roomId = null }) => {
  const isEditing = roomId !== null;
  const { t } = useLanguage();
  const MAX_IMAGES = 12;
  const MAX_IMAGE_SIZE_MB = 5;
  const navigate = useNavigate();
  const [searchParams]=useSearchParams();
  const inventoryRoomId=searchParams.get('source_room');
  const [inventoryLoading,setInventoryLoading]=useState(false);
  const [feeQuote, setFeeQuote] = useState(null);
  const [publishKey] = useState(newDraftId);
  const [sourceRoomId, setSourceRoomId] = useState(null);
  const [formData, setFormData] = useState({
    status: 'available',
    title: '',
    roomCode: '',
    description: '',
    address: '',
    city: 'Thành phố Hồ Chí Minh',
    district: '',
    ward: '',
    newWard: '',
    street: '',
    price: '',
    area: '',
    capacity: 1,
    room_type: 'room',
    furnishing: 'full',
    commission_rate: 0,
    is_exclusive: false,
    is_hot: false,
    utilities: BUILDING_UTILITIES.join(', '),
    furnishings: FURNISHING_ITEMS.join(', '),
    houseNumber: '',
    electricity: '',
    water: '',
    waterUnit: 'VNĐ/người/tháng',
    parking: '',
    serviceFee: '',
    otherFee: '',
    depositMonths: 1,
    reservationFee: '',
    additionalDeposit: 0,
    addDepositDeadline: 3,
    holdDays: 7,
    contractMonths: 12,
    maxVehicles: 2,
    pets: 'Hỏi quản lý',
    foreignGuests: 'Hỏi quản lý',
    originalPrice: '',
    depositCancellationFee: '',
    groupUrl: '',
    kinUrl: '',
    managerName: '',
    managerPhone: '',
    ownerName: '',
    ownerPhone: '',
    exclusiveDate: '',
    hotDate: '',
  });
  const [fetching, setFetching] = useState(isEditing);
  const [canManageRoom, setCanManageRoom] = useState(!isEditing);
  const [loadFailed, setLoadFailed] = useState(false);
  const [existingImages, setExistingImages] = useState([]);
  const savedDetailsRef = useRef({});
  const savedAddressRef = useRef(null);
  const [coordinates, setCoordinates] = useState(null);
  const [latitudeInput, setLatitudeInput] = useState('');
  const [longitudeInput, setLongitudeInput] = useState('');
  const [mapTargetCenter, setMapTargetCenter] = useState(defaultCenter);
  const [mapTargetZoom, setMapTargetZoom] = useState(13);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const previewUrlsRef = useRef([]);
  const fileInputRef = useRef(null);
  const districtLookupSeqRef = useRef(0);
  const districtToCitiesRef = useRef(new Map());
  const geocodeCacheRef = useRef({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingAI, setLoadingAI] = useState(false);
  const [addressData, setAddressData] = useState([]);
  const [addressDetails, setAddressDetails] = useState({});
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressDetailsLoading, setAddressDetailsLoading] = useState(false);


  useEffect(() => {
    if (!roomId) return undefined;
    let cancelled = false;
    setFetching(true);
    setLoadFailed(false);
    roomService.getRoom(roomId).then((room) => {
      if (cancelled) return;
      const currentUser = authService.getCurrentUser();
      setCanManageRoom(Boolean(currentUser && (currentUser.role === 'admin' || Number(currentUser.id) === Number(room.owner_id))));
      const stored = typeof room.details === 'string' ? JSON.parse(room.details) : (room.details || {});
      const legacy = {};
      const labels = { 'Số nhà': 'houseNumber', 'Phường mới': 'newWard', 'Phí điện': 'electricity', 'Phí nước': 'water', 'Phí gửi xe': 'parking', 'Phí dịch vụ': 'serviceFee', 'Phí khác': 'otherFee', 'Cọc': 'depositMonths', 'Cọc giữ chỗ': 'reservationFee', 'Số tiền cần bổ sung': 'additionalDeposit', 'Hợp đồng': 'contractMonths', 'Số xe tối đa': 'maxVehicles', 'Thú cưng': 'pets', 'Khách nước ngoài': 'foreignGuests' };
      String(room.description || '').split(/\r?\n/).forEach((line) => {
        const match = line.match(/^\s*([^:]+):\s*(.*?)\s*$/u);
        if (match && labels[match[1]]) legacy[labels[match[1]]] = match[2].replace(/\s+tháng$/u, '');
      });
      const details = { ...legacy, ...stored };
      savedDetailsRef.current = details;
      setFormData((previous) => {
        const next = { ...previous };
        Object.keys(previous).forEach((key) => {
          if (room[key] !== undefined && room[key] !== null) next[key] = room[key];
          if (details[key] !== undefined && details[key] !== null) next[key] = details[key];
        });
        const utilities = Array.isArray(room.utilities) ? room.utilities : [];
        next.utilities = utilities.filter((item) => !FURNISHING_ITEMS.includes(item) && !item.startsWith('Nước:')).join(', ');
        next.furnishings = utilities.filter((item) => FURNISHING_ITEMS.includes(item)).join(', ');
        if (!next.houseNumber) next.houseNumber = String(room.address || '').match(/^([0-9][0-9A-Za-z/.-]*)[ ,]/)?.[1] || '';
        savedAddressRef.current = Object.fromEntries(['address', 'houseNumber', 'street', 'ward', 'newWard', 'district', 'city'].map((key) => [key, next[key]]));
        next.price = String(Number(room.price) || 0);
        next.is_exclusive = Boolean(room.is_exclusive);
        next.is_hot = Boolean(room.is_hot);
        return next;
      });
      setExistingImages(Array.isArray(room.images) ? room.images : []);
      if (room.latitude != null && room.longitude != null) {
        const point = normalizeCoordinates(room.latitude, room.longitude);
        if (point) {
          setCoordinates(point);
          setLatitudeInput(String(point[0]));
          setLongitudeInput(String(point[1]));
        }
      }
    }).catch(() => {
      if (!cancelled) setLoadFailed(true);
    }).finally(() => {
      if (!cancelled) setFetching(false);
    });
    return () => { cancelled = true; };
  }, [roomId]);

  useEffect(() => {
    const province = addressData.find((item) => item.name === formData.city);
    if (!province || addressDetails[province.code]) return undefined;
    let cancelled = false;
    setAddressDetailsLoading(true);
    fetch(`https://provinces.open-api.vn/api/p/${province.code}?depth=3`)
      .then((response) => { if (!response.ok) throw new Error(); return response.json(); })
      .then((details) => { if (!cancelled) setAddressDetails((previous) => ({ ...previous, [province.code]: details })); })
      .catch(() => { if (!cancelled) setError('Không thể tải dữ liệu quận/huyện. Vui lòng thử lại.'); })
      .finally(() => { if (!cancelled) setAddressDetailsLoading(false); });
    return () => { cancelled = true; };
  }, [addressData, addressDetails, formData.city]);

  const syncNativeFileInput = (files) => {
    if (!fileInputRef.current) {
      return;
    }

    try {
      const dataTransfer = new DataTransfer();
      (files || []).forEach((file) => dataTransfer.items.add(file));
      fileInputRef.current.files = dataTransfer.files;
    } catch (error) {
      fileInputRef.current.value = '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((previous) => {
      const next = { ...previous, [name]: value };

      if (name === 'price' || name === 'depositMonths') {
        const price = parseMoneyValue(name === 'price' ? value : previous.price);
        const months = Math.max(0, Number(name === 'depositMonths' ? value : previous.depositMonths) || 0);
        next.reservationFee = price > 0 && months > 0 ? String(price * months) : '';
        next.additionalDeposit = 0;
      } else if (name === 'reservationFee') {
        const requiredDeposit = parseMoneyValue(previous.price) * (Number(previous.depositMonths) || 0);
        next.additionalDeposit = Math.max(0, requiredDeposit - parseMoneyValue(value));
      }

      return next;
    });
  };

  const handleAddressChange = (field, value) => {
    if (field === 'city') {
      setFormData((previous) => ({ ...previous, city: value, district: '', ward: '', newWard: '', street: '' }));
      const selected = addressData.find((province) => province.name === value);
      if (selected && !addressDetails[selected.code]) {
        const cachedDetails = sessionStorage.getItem(`vn-address-${selected.code}`);
        if (cachedDetails) {
          setAddressDetails((previous) => ({ ...previous, [selected.code]: JSON.parse(cachedDetails) }));
        } else {
          setAddressDetailsLoading(true);
          fetch(`https://provinces.open-api.vn/api/p/${selected.code}?depth=3`)
            .then((response) => {
              if (!response.ok) throw new Error('Không thể tải dữ liệu quận/huyện.');
              return response.json();
            })
            .then((details) => {
              sessionStorage.setItem(`vn-address-${selected.code}`, JSON.stringify(details));
              setAddressDetails((previous) => ({ ...previous, [selected.code]: details }));
            })
            .catch(() => setError('Không thể tải dữ liệu quận/huyện. Vui lòng thử lại.'))
            .finally(() => setAddressDetailsLoading(false));
        }
      }
      return;
    }

    if (field === 'district') {
      setFormData((previous) => ({ ...previous, district: value, ward: '', newWard: '', street: '' }));
      return;
    }

    if (field === 'ward') {
      const mappedWard = hcmWardMappings[formData.district]?.[value]?.[0] || '';
      setFormData((previous) => ({ ...previous, ward: value, newWard: mappedWard, street: '' }));
      return;
    }

    setFormData((previous) => ({ ...previous, [field]: value }));
  };

  const handleCoordinateCheck = () => {
    const latitude = Number(latitudeInput);
    const longitude = Number(longitudeInput);
    const valid = Number.isFinite(latitude)
      && Number.isFinite(longitude)
      && latitude >= vietnamBounds[0][0]
      && latitude <= vietnamBounds[1][0]
      && longitude >= vietnamBounds[0][1]
      && longitude <= vietnamBounds[1][1];

    if (!valid) {
      setError('Vui lòng nhập tọa độ hợp lệ tại Việt Nam. Vĩ độ: 8.3–23.6, kinh độ: 102–110.5.');
      return;
    }

    setError('');
    setCoordinates([latitude, longitude]);
    setMapTargetCenter([latitude, longitude]);
    setMapTargetZoom(17);
  };

  const handleMapCoordinatePick = (next) => {
    if (!next) {
      return;
    }

    setCoordinates(next);
    setLatitudeInput(String(next[0]));
    setLongitudeInput(String(next[1]));
  };

  const handleCoordinatePaste = (event) => {
    const pastedValue = event.clipboardData?.getData('text')?.trim() || '';
    const parts = pastedValue.split(',');

    if (parts.length !== 2) {
      return;
    }

    const latitude = parts[0].trim();
    const longitude = parts[1].trim();
    if (!latitude || !longitude || !Number.isFinite(Number(latitude)) || !Number.isFinite(Number(longitude))) {
      return;
    }

    event.preventDefault();
    setLatitudeInput(latitude);
    setLongitudeInput(longitude);
  };

  useEffect(() => {
    let cancelled = false;

    const loadAddressData = async () => {
      setAddressLoading(true);
      try {
        const cachedProvinces = sessionStorage.getItem('vn-provinces');
        const response = cachedProvinces ? null : await fetch('https://provinces.open-api.vn/api/?depth=1');
        if (response && !response.ok) {
          throw new Error('Không thể tải dữ liệu địa chỉ Việt Nam.');
        }
        const provinces = cachedProvinces ? JSON.parse(cachedProvinces) : await response.json();
        if (!cachedProvinces) {
          sessionStorage.setItem('vn-provinces', JSON.stringify(provinces));
        }
        if (!cancelled) {
          setAddressData(provinces);
        }
      } catch (loadError) {
        if (!cancelled) {
          setError('Không thể tải danh sách tỉnh/thành phố. Vui lòng kiểm tra kết nối mạng và thử lại.');
        }
      } finally {
        if (!cancelled) {
          setAddressLoading(false);
        }
      }
    };

    loadAddressData();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const province = addressData.find((item) => item.name === formData.city);
    if (!province || addressDetails[province.code]) {
      return undefined;
    }

    const cachedDetails = sessionStorage.getItem(`vn-address-${province.code}`);
    if (cachedDetails) {
      setAddressDetails((previous) => ({ ...previous, [province.code]: JSON.parse(cachedDetails) }));
      return undefined;
    }

    setAddressDetailsLoading(true);
    fetch(`https://provinces.open-api.vn/api/p/${province.code}?depth=3`)
      .then((response) => {
        if (!response.ok) throw new Error('Không thể tải dữ liệu quận/huyện.');
        return response.json();
      })
      .then((details) => {
        sessionStorage.setItem(`vn-address-${province.code}`, JSON.stringify(details));
        setAddressDetails((previous) => ({ ...previous, [province.code]: details }));
      })
      .catch(() => setError('Không thể tải dữ liệu quận/huyện. Vui lòng thử lại.'))
      .finally(() => setAddressDetailsLoading(false));

    return undefined;
  }, [addressData, addressDetails, formData.city]);

  useEffect(() => {
    return () => {
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  useEffect(() => {
    try {
      const saved = localStorage.getItem('districtGeocodeCacheV4');
      geocodeCacheRef.current = saved ? JSON.parse(saved) : {};
    } catch (cacheError) {
      geocodeCacheRef.current = {};
    }

    let cancelled = false;

    const loadAdministrativeIndex = async () => {
      try {
        const response = await fetch('https://provinces.open-api.vn/api/?depth=2');
        if (!response.ok || cancelled) {
          return;
        }

        const data = await response.json();
        if (!Array.isArray(data) || cancelled) {
          return;
        }

        const nextIndex = new Map();
        data.forEach((province) => {
          const provinceName = String(province?.name || '').trim();
          const districts = Array.isArray(province?.districts) ? province.districts : [];

          districts.forEach((district) => {
            const normalized = normalizeAdministrativeName(district?.name || '');
            if (!normalized || !provinceName) {
              return;
            }

            const existing = nextIndex.get(normalized) || [];
            if (!existing.includes(provinceName)) {
              nextIndex.set(normalized, [...existing, provinceName]);
            }
          });
        });

        districtToCitiesRef.current = nextIndex;
      } catch (indexError) {
        // Keep working with geocode-only mode if the index API is unavailable.
      }
    };

    loadAdministrativeIndex();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (Array.isArray(coordinates) && coordinates.length === 2) {
      setMapTargetCenter(clampToVietnamBounds(coordinates));
      setMapTargetZoom(17);
      return;
    }

    const cityCenter = cityCoordinates[normalizeCity(formData.city)] || defaultCenter;
    setMapTargetCenter(clampToVietnamBounds(cityCenter));
    setMapTargetZoom(13);
  }, [coordinates, formData.city]);

  useEffect(() => {
    const districtRaw = String(formData.district || '').trim();
    const cityRaw = String(formData.city || '').trim();
    const cityQueryText = getCityQueryText(cityRaw);
    const districtText = normalizeDistrict(districtRaw);
    const cityText = normalizeCity(cityRaw);
    const districtKey = normalizeAdministrativeName(districtRaw);

    if (!districtText) {
      return undefined;
    }

    const timeoutId = window.setTimeout(async () => {
      districtLookupSeqRef.current += 1;
      const lookupId = districtLookupSeqRef.current;

      const fallbackCenter = resolveDistrictFallbackCenter(districtRaw, cityRaw);
      if (fallbackCenter) {
        setMapTargetCenter(clampToVietnamBounds(fallbackCenter));
        setMapTargetZoom(15);
        return;
      }

      const inferredCities = cityRaw
        ? []
        : (districtToCitiesRef.current.get(districtKey) || []);

      let districtQuery = districtRaw;
      if (/^\d{1,2}$/.test(districtText)) {
        districtQuery = `quan ${districtText}`;
      }

      const districtQueryCandidates = [];

      if (districtQuery) {
        districtQueryCandidates.push(`${districtQuery}, Vietnam`);
      }

      inferredCities.slice(0, 4).forEach((cityName) => {
        districtQueryCandidates.push(`${districtQuery}, ${cityName}, Vietnam`);
      });

      if (districtQuery && cityQueryText) {
        districtQueryCandidates.push(`${districtQuery}, ${cityQueryText}, Vietnam`);
      }

      if (districtText) {
        districtQueryCandidates.push(`${districtText}, ${cityText || ''}, Vietnam`.replace(/,\s*,/g, ',').replace(/,\s*$/, ''));
      }

      const uniqueDistrictQueries = [...new Set(districtQueryCandidates.map((item) => item.trim()).filter(Boolean))];

      try {
        let districtCenter = null;

        for (const query of uniqueDistrictQueries) {
          const cacheKey = normalizeDistrict(`district:${query}`);
          const cached = geocodeCacheRef.current[cacheKey];
          if (Array.isArray(cached) && cached.length === 2) {
            const cachedLat = Number(cached[0]);
            const cachedLng = Number(cached[1]);
            if (Number.isFinite(cachedLat) && Number.isFinite(cachedLng)) {
              districtCenter = clampToVietnamBounds([cachedLat, cachedLng]);
              break;
            }
          }

          const response = await fetch(
            `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=5&addressdetails=1&polygon_geojson=1&accept-language=vi&countrycodes=vn&q=${encodeURIComponent(query)}`,
            {
              headers: {
                Accept: 'application/json',
              },
            }
          );

          if (!response.ok || lookupId !== districtLookupSeqRef.current) {
            continue;
          }

          const data = await response.json();
          const districtScopedData = filterResultsByAdmin(
            data,
            districtRaw,
            cityRaw,
            Boolean(cityRaw)
          );
          if (districtScopedData.length === 0) {
            continue;
          }

          const selected = pickBestGeocodeResult(districtScopedData, '', districtRaw, cityRaw);
          const resolvedCenter = resolveGeocodeCenter(selected);

          if (!resolvedCenter) {
            continue;
          }

          geocodeCacheRef.current[cacheKey] = [resolvedCenter[0], resolvedCenter[1]];
          try {
            localStorage.setItem('districtGeocodeCacheV4', JSON.stringify(geocodeCacheRef.current));
          } catch (persistError) {
            // Ignore storage quota issues.
          }

          districtCenter = resolvedCenter;
          break;
        }

        if (districtCenter) {
          setMapTargetCenter(districtCenter);
          setMapTargetZoom(15);
          return;
        }

        if (districtCenter) {
          setMapTargetCenter(districtCenter);
          setMapTargetZoom(15);
        }
      } catch (lookupError) {
        // Silently ignore geocoding errors and keep the current map view.
      }
    }, 450);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [formData.district, formData.city]);

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files || []);

    if (files.length === 0) {
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      previewUrlsRef.current = [];
      setSelectedFiles([]);
      setImagePreviews([]);
      syncNativeFileInput([]);
      return;
    }

    if (existingImages.length + files.length > MAX_IMAGES) {
      setError(t('createRoom.maxImagesError', { max: MAX_IMAGES }));
      e.target.value = '';
      return;
    }

    const oversize = files.find((file) => file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024);
    if (oversize) {
      setError(t('createRoom.maxImageSizeError', { size: MAX_IMAGE_SIZE_MB }));
      e.target.value = '';
      return;
    }

    try {
      setError('');
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      const nextPreviews = files.map((file) => URL.createObjectURL(file));
      previewUrlsRef.current = nextPreviews;
      setSelectedFiles(files);
      setImagePreviews(nextPreviews);
      syncNativeFileInput(files);
    } catch (err) {
      setError(t('createRoom.readImageFailed'));
    }
  };

  const handleRemoveSelectedImage = (indexToRemove) => {
    setSelectedFiles((prev) => {
      const nextFiles = prev.filter((_, index) => index !== indexToRemove);
      syncNativeFileInput(nextFiles);
      return nextFiles;
    });
    setImagePreviews((prev) => {
      const toRemove = prev[indexToRemove];
      if (toRemove) {
        URL.revokeObjectURL(toRemove);
      }
      const nextPreviews = prev.filter((_, index) => index !== indexToRemove);
      previewUrlsRef.current = nextPreviews;
      return nextPreviews;
    });
  };

  const handleGenerateAIDescription = async () => {
    setLoadingAI(true);
    setError('');

    try {
      const result = await aiService.generateRoomDescription({
        title: formData.title,
        room_type: formData.room_type,
        address: [
          formData.houseNumber,
          formData.street,
          formData.ward,
          formData.newWard,
          formData.district,
          formData.city,
        ].filter(Boolean).join(', '),
        price: String(formData.price ?? ''),
        electricity_fee: String(formData.electricity ?? ''),
        water_fee: formData.water ? `${formData.water} ${formData.waterUnit}` : '',
        parking_fee: String(formData.parking ?? ''),
        service_fee: String(formData.serviceFee ?? ''),
        other_fee: String(formData.otherFee ?? ''),
        selected_furnitures: formData.furnishings.split(',').map((item) => item.trim()).filter(Boolean),
        building_amenities: formData.utilities.split(',').map((item) => item.trim()).filter(Boolean),
        deposit_months: String(formData.depositMonths ?? ''),
        holding_deposit: String(formData.reservationFee ?? ''),
        contract_months: String(formData.contractMonths ?? ''),
        max_people: String(formData.capacity ?? ''),
        max_vehicles: String(formData.maxVehicles ?? ''),
        pets_policy: formData.pets,
      });

      setFormData((previous) => ({ ...previous, description: result.description || '' }));
    } catch (generationError) {
      setError(generationError.response?.data?.message || 'Không thể tạo mô tả bằng AI. Vui lòng thử lại.');
    } finally {
      setLoadingAI(false);
    }
  };

  const importProjectRoom = (project, room = {}) => {
    const hasRoom = Boolean(room.id);
    setSourceRoomId(hasRoom ? room.id : null);
    const d = project.details || {};
    const details = room.details || {};
    setFormData((previous) => {
      const next = { ...previous };
      Object.keys(previous).forEach((key) => {
        if (room[key] !== undefined && room[key] !== null) next[key] = room[key];
        if (details[key] !== undefined && details[key] !== null) next[key] = details[key];
      });
      Object.assign(next, {
        city: d.city || room.city || '', district: d.district || room.district || '', ward: d.ward || '',
        newWard: d.new_ward || '', street: d.street || room.street || '', houseNumber: d.house_number || '',
        address: project.address, managerName: d.manager_name || '', managerPhone: d.manager_phone || '',
        ownerName: d.landlord_name || '', ownerPhone: d.landlord_phone || '',
        commission_rate: d.commission_rate ?? room.commission_rate ?? 0,
        status: hasRoom ? (room.status === 'available' ? 'available' : 'rented') : previous.status,
        utilities: (d.amenities ? projectAmenities.filter(([key]) => d.amenities.includes(key)).map(([, label]) => label) : (room.utilities || [])).join(', '),
        furnishings: hasRoom ? (details.furnishings || '') : furnishings.filter(([key]) => d.furnishing === 'full' || (d.furnishings || []).includes(key)).map(([, label]) => label).join(', '),
        furnishing: hasRoom ? room.furnishing : (d.furnishing === 'custom' ? 'basic' : d.furnishing || previous.furnishing),
        price: hasRoom ? String(room.price ?? '') : previous.price, roomCode: hasRoom ? (details.roomCode || '') : previous.roomCode,
        electricity: d.electricity_fee ?? details.electricity ?? '', water: d.water_fee ?? details.water ?? '',
        waterUnit: { person: 'VNĐ/người/tháng', m3: 'VNĐ/m³', room: 'VNĐ/phòng/tháng' }[d.water_unit] || details.waterUnit || 'VNĐ/người/tháng',
        parking: d.parking_fee ?? details.parking ?? '', serviceFee: d.management_fee ?? details.serviceFee ?? '', otherFee: d.other_fee ?? details.otherFee ?? '',
        depositMonths: d.deposit_months ?? details.depositMonths ?? 1, contractMonths: d.lease_months ?? details.contractMonths ?? 12,
        reservationFee: String(parseMoneyValue(hasRoom ? room.price : previous.price) * Number(d.deposit_months ?? details.depositMonths ?? 1)),
        pets: { yes: 'Cho phép', no: 'Không', ask: 'Hỏi quản lý' }[d.pets] || details.pets || 'Hỏi quản lý',
        foreignGuests: { yes: 'Cho phép', no: 'Không', ask: 'Hỏi quản lý' }[d.foreign_tenants] || details.foreignGuests || 'Hỏi quản lý',
      });
      return next;
    });
    if (hasRoom) {
      setSelectedFiles([]); setImagePreviews([]); syncNativeFileInput([]);
      setExistingImages(room.images || []);
    }
    const point = normalizeCoordinates(d.latitude ?? room.latitude, d.longitude ?? room.longitude);
    setCoordinates(point);
    setLatitudeInput(point ? String(point[0]) : ''); setLongitudeInput(point ? String(point[1]) : '');
    if (point) { setMapTargetCenter(point); setMapTargetZoom(17); }
  };

  useEffect(()=>{
    if(isEditing||!inventoryRoomId)return;
    let active=true;setInventoryLoading(true);
    projectService.inventoryRoom(inventoryRoomId).then(({project,room})=>{if(active)importProjectRoom(project,room);}).catch(()=>{if(active)setError('Không lấy được thông tin phòng hoặc quyền sử dụng dự án đã thay đổi.');}).finally(()=>{if(active)setInventoryLoading(false);});
    return()=>{active=false;};
  // Import only when entering a different source room, not after local edits.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[inventoryRoomId,isEditing]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const submitData = new FormData();
      if (!isEditing) {
        if (!feeQuote || !feeQuote.accepted) throw new Error('Vui lòng kiểm tra và xác nhận phí đăng tin.');
        submitData.append('request_key', publishKey);
        submitData.append('quoted_price', feeQuote.price);
        submitData.append('quoted_days', feeQuote.duration_days);
        submitData.append('accept_fee', feeQuote.accepted ? '1' : '0');
        if (sourceRoomId) submitData.append('source_room_id', sourceRoomId);
      }
      submitData.append('title', formData.title);
      submitData.append('description', formData.description);
      const generatedAddress = [
        formData.houseNumber,
        formData.street,
        formData.ward,
        formData.district,
        formData.city,
      ].filter(Boolean).join(', ');
      const addressUnchanged = isEditing && savedAddressRef.current && ['houseNumber', 'street', 'ward', 'newWard', 'district', 'city'].every((key) => formData[key] === savedAddressRef.current[key]);
      submitData.append('address', addressUnchanged ? savedAddressRef.current.address : (generatedAddress || formData.address));
      submitData.append('city', formData.city);
      submitData.append('district', formData.district);
      submitData.append('ward', formData.newWard || formData.ward);
      submitData.append('street', formData.street);
      submitData.append('price', String(parseMoneyValue(formData.price)));
      submitData.append('area', formData.area);
      submitData.append('capacity', String(formData.capacity));
      submitData.append('room_type', formData.room_type);
      submitData.append('furnishing', formData.furnishing);
      submitData.append('commission_rate', String(formData.commission_rate));
      submitData.append('is_exclusive', formData.is_exclusive ? '1' : '0');
      submitData.append('is_hot', formData.is_hot ? '1' : '0');
      submitData.append('status', formData.status);
      submitData.append('details', JSON.stringify({
        ...savedDetailsRef.current,
        roomCode: formData.roomCode.trim(),
        ...Object.fromEntries(['houseNumber', 'ward', 'newWard', 'originalPrice', 'depositCancellationFee', 'groupUrl', 'kinUrl', 'managerName', 'managerPhone', 'ownerName', 'ownerPhone', 'exclusiveDate', 'hotDate'].map((key) => [key, formData[key]])),
        electricity: formData.electricity || '',
        water: formData.water || '',
        waterUnit: formData.waterUnit || '',
        parking: formData.parking || '',
        serviceFee: formData.serviceFee || '',
        otherFee: formData.otherFee || '',
        depositMonths: formData.depositMonths ?? '',
        reservationFee: formData.reservationFee || '',
        additionalDeposit: formData.additionalDeposit || 0,
        addDepositDeadline: formData.addDepositDeadline ?? '',
        holdDays: formData.holdDays ?? '',
        contractMonths: formData.contractMonths ?? '',
        maxVehicles: formData.maxVehicles ?? '',
        pets: formData.pets || '',
        foreignGuests: formData.foreignGuests || '',
      }));

      if (Array.isArray(coordinates) && coordinates.length === 2) {
        const next = normalizeCoordinates(coordinates[0], coordinates[1]);
        if (next) {
          submitData.append('latitude', String(next[0]));
          submitData.append('longitude', String(next[1]));
        }
      }

      const utilities = [
        ...(formData.utilities ? formData.utilities.split(',').map((u) => u.trim()).filter(Boolean) : []),
        ...(formData.furnishings ? formData.furnishings.split(',').map((item) => item.trim()).filter(Boolean) : []),
      ];
      if (formData.water) {
        utilities.push(`Nước: ${formData.water} ${formData.waterUnit}`);
      }
      if (isEditing && !utilities.length) submitData.append('utilities', '');
      [...new Set(utilities)].forEach((utility) => {
        submitData.append('utilities[]', utility);
      });

      if (isEditing || existingImages.length) {
        if (!existingImages.length) submitData.append('images', '');
        existingImages.forEach((image) => submitData.append('images[]', image));
      }
      selectedFiles.forEach((file) => {
        submitData.append('image_files[]', file);
      });
      
      const createdRoom = isEditing
        ? await roomService.updateRoom(roomId, submitData)
        : await roomService.createRoom(submitData);
      if (isEditing) {
        localStorage.setItem('myRoomsNotice', 'Cập nhật phòng thành công.');
        navigate('/my-rooms');
        return;
      }
      if (createdRoom?.id) {
        localStorage.setItem(
          'myRoomsNotice',
          `Đăng phòng thành công. Mã phòng: ${createdRoom.listing_code || 'đang cập nhật'}`,
        );
      } else {
        localStorage.setItem('myRoomsNotice', t('createRoom.createSuccess'));
      }
      navigate('/my-rooms');
    } catch (err) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message;
      const errors = err?.response?.data?.errors;

      if (errors && typeof errors === 'object') {
        const firstKey = Object.keys(errors)[0];
        const firstMsg = Array.isArray(errors[firstKey]) ? errors[firstKey][0] : '';
        setError(translateApiMessage(t, firstMsg, 'createRoom.invalidData'));
      } else if (status === 401) {
        setError(t('createRoom.sessionExpired'));
      } else if (status === 403) {
        setError(t('createRoom.noPermission'));
      } else if (status === 413) {
        setError(t('createRoom.imageTooLarge'));
      } else if (message) {
        setError(translateApiMessage(t, message, isEditing ? 'editRoom.updateFailed' : 'createRoom.createFailed'));
      } else {
        setError(t(isEditing ? 'editRoom.updateFailed' : 'createRoom.createFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const selectedProvince = addressData.find((province) => province.name === formData.city);
  const selectedProvinceDetails = selectedProvince ? (addressDetails[selectedProvince.code] || selectedProvince) : null;
  const selectedDistrict = selectedProvinceDetails?.districts?.find((district) => district.name === formData.district);
  const districtWardOptions = selectedDistrict?.wards || [];
  const scopedNewWardOptions = sortVietnamese([...new Set(districtWardOptions.flatMap((ward) => (
    normalizeCity(formData.city) === normalizeCity('Thành phố Hồ Chí Minh')
      ? (hcmWardMappings[formData.district]?.[ward.name] || [])
      : [ward.name]
  )))]);
  const furnishingItems = FURNISHING_ITEMS;

  if (fetching) return <div className="loading">{t('common.loading')}</div>;
  if (loadFailed) return <div className="error-message">{t('editRoom.loadFailed')}</div>;
  const imageBase = (process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api').replace(/\/api\/?$/, '');
  const resolveImage = (path) => /^https?:|^data:/i.test(path) ? path : `${imageBase}/${path.replace(/^\//, '')}`;

  return (
    <div className="form-container form-container-wide room-create-page">
      <div className="room-create-header">
        <div>
          <span className="room-create-kicker">Quản lý bất động sản</span>
          <h2 className="form-title">{t(isEditing ? 'editRoom.title' : 'createRoom.title')}</h2>
          <p className="room-create-subtitle">{isEditing ? 'Cập nhật hình ảnh, thông tin phòng và chính sách của tin đăng.' : 'Tạo tin đăng mới với đầy đủ hình ảnh, thông tin phòng và chính sách.'}</p>
        </div>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      <form onSubmit={handleSubmit}>
        <section className="room-create-section room-photo-section">
          <div className="room-create-section-heading">
            <div>
              <span className="room-section-number">01</span>
              <div>
                <h3>Quản lý bộ ảnh</h3>
                <p>Thêm tối đa {MAX_IMAGES} ảnh, dung lượng mỗi ảnh không quá {MAX_IMAGE_SIZE_MB}MB.</p>
              </div>
            </div>
            <button type="button" className="room-add-image-button" onClick={() => fileInputRef.current?.click()}>
              + Thêm bộ ảnh
            </button>
          </div>
          <div className="room-photo-toolbar">
            <span>{selectedFiles.length ? `${selectedFiles.length} ảnh đã chọn` : 'Chưa có bộ ảnh nào.'}</span>
            <small>Ảnh đầu tiên sẽ được dùng làm ảnh đại diện.</small>
          </div>
          {existingImages.length > 0 && (
            <div className="room-photo-grid">
              {existingImages.map((image, index) => (
                <div key={`existing-${index}`} className="room-photo-item">
                  <img src={resolveImage(image)} alt={`Ảnh hiện có ${index + 1}`} />
                  <button type="button" onClick={() => setExistingImages((previous) => previous.filter((_, i) => i !== index))} aria-label={`X?a Ảnh hiện có ${index + 1}`}>?</button>
                </div>
              ))}
            </div>
          )}
          {imagePreviews.length > 0 && (
            <div className="room-photo-grid">
              {imagePreviews.map((image, index) => (
                <div key={`top-preview-${index}`} className="room-photo-item">
                  <img src={image} alt={`Ảnh phòng ${index + 1}`} />
                  <button type="button" onClick={() => handleRemoveSelectedImage(index)} aria-label={`Xóa ảnh ${index + 1}`}>×</button>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="room-create-section room-info-section">
          <div className="room-create-section-title">
            <span className="room-section-number">02</span>
            <div>
              <h3>Thông tin tin đăng</h3>
              <p>Thông tin dự án, địa chỉ, loại phòng, tiện ích và chính sách.</p>
              </div>
            </div>
        <div className="room-create-panels">
            <div className="room-create-panel">
              <h4>Tổng quan & Địa chỉ</h4>
              {!isEditing&&authService.getCurrentUser()?.role==='saler'&&<p><Link to="/inventory">Chọn phòng từ kho dự án được chia sẻ</Link></p>}
              {inventoryLoading&&<p role="status">Đang lấy thông tin phòng dự án…</p>}
              {!isEditing && <ListingProjectImport onSelect={importProjectRoom} onProject={importProjectRoom} onClear={() => setSourceRoomId(null)} />}
              <div className="form-group">
                <label className="form-label" htmlFor="listing-room-code">Mã phòng</label>
                <input id="listing-room-code" type="text" name="roomCode" className="form-input" value={formData.roomCode} onChange={handleChange} maxLength={100} placeholder="Ví dụ: A101, P.203" />
              </div>
              <div className="form-group">
                <label className="form-label">{t('createRoom.fieldTitle')}</label>
                <input type="text" name="title" className="form-input" value={formData.title} onChange={handleChange} maxLength={255} required />
              </div>
              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">{t('createRoom.fieldPrice', { currency: 'VNĐ', period: t('createRoom.perMonth') })}</label>
                  <input type="text" name="price" className="form-input" value={formData.price} onChange={handleChange} inputMode="numeric" autoComplete="on" required placeholder="Ví dụ: 4.900.000" />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('createRoom.fieldArea')}</label>
                  <input type="number" name="area" className="form-input" value={formData.area} onChange={handleChange} required min="0.1" step="0.1" placeholder="Ví dụ: 25" />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('createRoom.fieldCapacity')}</label>
                  <input type="number" name="capacity" className="form-input" value={formData.capacity} onChange={handleChange} required min="1" step="1" />
                </div>
                <div className="form-group">
                  <label className="form-label">Dạng phòng</label>
                  <select name="room_type" className="form-select" value={formData.room_type} onChange={handleChange} required>
                    <option value="room">Tất cả</option>
                    <option value="gac">Gác</option>
                    <option value="studio">Studio</option>
                    <option value="duplex">Duplex</option>
                    <option value="studio_separate">Studio tách bếp</option>
                    <option value="one_bedroom">1PN</option>
                    <option value="two_bedroom">2PN</option>
                    <option value="three_bedroom">3PN</option>
                    <option value="apartment">Chung cư</option>
                    <option value="whole_house">Nhà nguyên căn</option>
                    <option value="commercial">Mặt bằng</option>
                  </select>
                </div>
              </div>
              <div className="form-grid-2">
                <div className="form-group"><label className="form-label">Tỉnh/Thành phố</label><select className="form-select" value={formData.city} onChange={(event) => handleAddressChange('city', event.target.value)} required><option value="">{addressLoading ? 'Đang tải dữ liệu...' : 'Chọn tỉnh/thành phố'}</option>{sortVietnamese([...new Set([addressData.map((province) => province.name), formData.city].flat().filter(Boolean))]).map((provinceName) => <option key={provinceName} value={provinceName}>{provinceName}</option>)}</select></div>
                <div className="form-group"><label className="form-label">Quận/Huyện</label><select className="form-select" value={formData.district} onChange={(event) => handleAddressChange('district', event.target.value)} disabled={(!selectedProvince && !formData.district) || addressDetailsLoading} required><option value="">{addressDetailsLoading ? 'Đang tải quận/huyện...' : 'Chọn quận/huyện'}</option>{sortVietnamese([...new Set([(selectedProvinceDetails?.districts || []).map((district) => district.name), formData.district].flat().filter(Boolean))]).map((districtName) => <option key={districtName} value={districtName}>{districtName}</option>)}</select></div>
                <div className="form-group"><label className="form-label">Phường/Xã</label><select className="form-select" value={formData.ward} onChange={(event) => handleAddressChange('ward', event.target.value)} disabled={!selectedDistrict && !formData.ward} required><option value="">Chọn phường/xã</option>{sortVietnamese([...new Set([(selectedDistrict?.wards || []).map((ward) => ward.name), formData.ward].flat().filter(Boolean))]).map((wardName) => <option key={wardName} value={wardName}>{wardName}</option>)}</select></div>
                <div className="form-group"><label className="form-label">Đường</label><input className="form-input" name="street" value={formData.street} onChange={handleChange} placeholder="Nhập tên đường..." /></div>
                <div className="form-group"><label className="form-label">Số nhà</label><input className="form-input" name="houseNumber" value={formData.houseNumber} onChange={handleChange} placeholder="41/10A..." /></div>
                <div className="form-group"><label className="form-label">Phường (mới)</label><select className="form-select" name="newWard" value={formData.newWard} onChange={(event) => handleAddressChange('newWard', event.target.value)} disabled={!selectedDistrict && !formData.ward}><option value="">Chọn phường mới</option>{[...new Set([...scopedNewWardOptions, formData.newWard].filter(Boolean))].map((wardName) => <option key={wardName} value={wardName}>{wardName}</option>)}</select></div>
              </div>
              <RoomAvailabilitySwitch status={formData.status} canManage={canManageRoom} onChange={(status) => setFormData((previous) => ({ ...previous, status }))} />
              <div className="form-grid-2">
                <div className="form-group"><label className="form-label">Vĩ độ (chính xác)</label><input className="form-input" type="number" step="any" value={latitudeInput} onChange={(event) => setLatitudeInput(event.target.value)} onPaste={handleCoordinatePaste} placeholder="Ví dụ: 10.823100" /></div>
                <div className="form-group"><label className="form-label">Kinh độ (chính xác)</label><input className="form-input" type="number" step="any" value={longitudeInput} onChange={(event) => setLongitudeInput(event.target.value)} onPaste={handleCoordinatePaste} placeholder="Ví dụ: 106.629700" /></div>
              </div>
              <button type="button" className="room-address-check" onClick={handleCoordinateCheck}>Kiểm tra thông tin địa chỉ</button>
            </div>
            <div className="room-create-panel">
              <h4>Phụ phí</h4>
              <div className="form-grid-2">
                {[
                  ['electricity', 'Điện', '4.000'],
                  ['water', 'Tiền nước', '100.000'],
                  ['parking', 'Gửi xe', 'Theo loại xe'],
                  ['serviceFee', 'Dịch vụ', '150.000'],
                ].map(([name, label, placeholder]) => (
                  <div className="form-group" key={name}><label className="form-label">{label}{name === 'water' ? ' (đơn giá)' : ''}</label><input className="form-input" name={name} value={formData[name]} onChange={handleChange} placeholder={placeholder} /></div>
                ))}
                <div className="form-group">
                  <label className="form-label">Đơn vị tiền nước</label>
                  <select className="form-select" name="waterUnit" value={formData.waterUnit} onChange={handleChange}>
                    <option value="VNĐ/người/tháng">VNĐ/người/tháng</option>
                    <option value="VNĐ/m³">VNĐ/m³ (theo khối)</option>
                    <option value="VNĐ/phòng/tháng">VNĐ/phòng/tháng</option>
                  </select>
                </div>
              </div>
              <div className="form-group"><label className="form-label">Phí khác</label><textarea className="form-textarea" name="otherFee" value={formData.otherFee} onChange={handleChange} placeholder="VD: Rác 50k..." /></div>
            </div>
            <div className="room-create-panel">
              <h4>Chính sách & Quy định</h4>
              <div className="form-grid-2">
                <div className="form-group"><label className="form-label">Cọc (số tháng)</label><input className="form-input" type="number" name="depositMonths" value={formData.depositMonths} onChange={handleChange} min="0" step="0.5" /></div>
                <div className="form-group"><label className="form-label">Số tiền cọc</label><input className="form-input" name="reservationFee" value={formData.reservationFee} onChange={handleChange} placeholder="2.000.000" /></div>
                <div className="form-group"><label className="form-label">Thời hạn bổ sung cọc</label><input className="form-input" type="number" name="addDepositDeadline" value={formData.addDepositDeadline} onChange={handleChange} /></div>
                <div className="form-group"><label className="form-label">Thời gian giữ phòng</label><input className="form-input" type="number" name="holdDays" value={formData.holdDays} onChange={handleChange} /></div>
                <div className="form-group"><label className="form-label">Hợp đồng (số tháng)</label><input className="form-input" type="number" name="contractMonths" value={formData.contractMonths} onChange={handleChange} /></div>
                <div className="form-group"><label className="form-label">Số xe tối đa</label><input className="form-input" type="number" name="maxVehicles" value={formData.maxVehicles} onChange={handleChange} /></div>
                <div className="form-group"><label className="form-label">Thú cưng</label><select className="form-select" name="pets" value={formData.pets} onChange={handleChange}><option>Hỏi quản lý</option><option>Cho phép</option><option>Không</option></select></div>
                <div className="form-group"><label className="form-label">Khách nước ngoài</label><select className="form-select" name="foreignGuests" value={formData.foreignGuests} onChange={handleChange}><option>Hỏi quản lý</option><option>Cho phép</option><option>Không</option></select></div>
              </div>
            </div>
            <div className="room-create-panel">
              <h4>Tiện ích & An toàn</h4>
              <div className="form-grid-2 room-amenity-grid">
                {BUILDING_UTILITIES.map((utility) => (
                  <label className="amenity-checkbox" key={utility}>
                    <input type="checkbox" checked={formData.utilities.split(',').map((item) => item.trim()).includes(utility)} onChange={(event) => {
                      const current = formData.utilities.split(',').map((item) => item.trim()).filter(Boolean);
                      const next = event.target.checked ? [...current, utility] : current.filter((item) => item !== utility);
                      setFormData({ ...formData, utilities: next.join(', ') });
                    }} />
                    {utility}
                  </label>
                ))}
              </div>
            </div>
            <div className="room-create-panel">
              <h4>Nội thất</h4>
              <div className="form-grid-2 room-amenity-grid">
                {[
                  ['full', 'Full'],
                  ['empty', 'Trống'],
                ].map(([value, label]) => (
                  <label className="amenity-checkbox" key={value}>
                    <input
                      type="checkbox"
                      checked={formData.furnishing === value}
                      onChange={() => setFormData({
                        ...formData,
                        furnishing: value,
                        furnishings: value === 'full' ? furnishingItems.join(', ') : '',
                      })}
                    />
                    {label}
                  </label>
                ))}
                {furnishingItems.map((item) => (
                  <label className="amenity-checkbox" key={item}>
                    <input type="checkbox" checked={formData.furnishings.split(',').map((value) => value.trim()).includes(item)} onChange={(event) => {
                      const current = formData.furnishings.split(',').map((value) => value.trim()).filter(Boolean);
                      const next = event.target.checked ? [...current, item] : current.filter((value) => value !== item);
                      setFormData({ ...formData, furnishing: 'basic', furnishings: next.join(', ') });
                    }} />
                    {item}
                  </label>
                ))}
              </div>
            </div>
        </div>
        <div className="form-group room-description-section">
          <div className="room-description-label">
            <label className="form-label">{t('createRoom.fieldDescription')}</label>
            <button type="button" className="room-ai-description-button" onClick={handleGenerateAIDescription} disabled={loadingAI}>
              ✨ {loadingAI ? 'Đang viết...' : 'AI viết mô tả'}
            </button>
          </div>
          <textarea name="description" className="form-textarea room-description-textarea" value={formData.description} onChange={handleChange} maxLength={9999} required />
        </div>
        <div className="form-group">
          <label className="form-label">{t('createRoom.mapTitle')}</label>
          <div className="location-picker-shell">
            <MapContainer
              center={mapTargetCenter}
              zoom={13}
              scrollWheelZoom
              className="location-picker-map"
              maxBounds={vietnamBounds}
              maxBoundsViscosity={1.0}
              minZoom={5}
              maxZoom={18}
              worldCopyJump={false}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://tile.openstreetmap.de/{z}/{x}/{y}.png"
              />
              <MapViewportController center={mapTargetCenter} zoom={mapTargetZoom} />
              <MapClickHandler onPick={handleMapCoordinatePick} />
              {coordinates && (
                <Marker
                  position={coordinates}
                  icon={pickerIcon}
                  draggable
                  eventHandlers={{
                    dragend: (event) => {
                      const marker = event.target;
                      const position = marker.getLatLng();
                      const next = normalizeCoordinates(position.lat, position.lng);
                      if (next) {
                        handleMapCoordinatePick(next);
                      }
                    },
                  }}
                />
              )}
            </MapContainer>
            <p className="location-picker-note">
              {t('createRoom.mapHint')}
            </p>
            <div className="location-picker-coordinates">
              <span>{t('createRoom.latitude')}: {latitudeInput || t('createRoom.notSelected')}</span>
              <span>{t('createRoom.longitude')}: {longitudeInput || t('createRoom.notSelected')}</span>
            </div>
          </div>
        </div>

        <div className="form-group room-hidden-image-picker">
          <label className="form-label">{t('createRoom.fieldImages', { max: MAX_IMAGES, size: MAX_IMAGE_SIZE_MB })}</label>
          <input
            ref={fileInputRef}
            type="file"
            className="form-input"
            accept="image/*"
            multiple
            onChange={handleImageChange}
          />
          <small className="helper-link">{t('createRoom.selectedCount', { count: selectedFiles.length })}</small>

          {imagePreviews.length > 0 && (
            <div className="image-preview-grid">
              {imagePreviews.map((image, index) => (
                <div key={`preview-${index}`} className="image-preview-item" style={{ position: 'relative' }}>
                  <img src={image} alt={`Preview ${index + 1}`} />
                  <button
                    type="button"
                    className="btn btn-danger"
                    style={{ position: 'absolute', top: 8, right: 8, padding: '0.2rem 0.45rem' }}
                    onClick={() => handleRemoveSelectedImage(index)}
                    aria-label={t('createRoom.removeSelectedImageAria', { index: index + 1 })}
                  >
                    X
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        </section>

        {isEditing && <div className="form-group">
          <label className="form-label">{t('editRoom.fieldStatus')}</label>
          <select name="status" className="form-select" value={formData.status} onChange={handleChange} disabled={!canManageRoom}>
            <option value="available">Còn trống</option><option value="soon_available">Sắp trống</option><option value="transfer">Nhượng</option><option value="rented">Đã thuê</option><option value="closed">Đã chốt</option><option value="reserved">Cọc / Giữ chỗ</option><option value="maintenance">Bảo trì</option>
          </select>
        </div>}
        {!isEditing && <ListingFee onChange={setFeeQuote} />}
        <button type="submit" className="form-button" disabled={loading || !canManageRoom || (!isEditing && (!feeQuote?.accepted || Number(feeQuote.balance) < Number(feeQuote.price)))}>
          {loading ? t(isEditing ? 'editRoom.updating' : 'createRoom.creating') : t(isEditing ? 'editRoom.submit' : 'createRoom.submit')}
        </button>
      </form>
    </div>
  );
};

export default CreateRoom;
